import React, { useEffect, useState, RefObject, useMemo } from 'react';
import useOrganizationSettings from '@core/hooks/useOrganizationSettings';
import useSnackbar from '@core/hooks/useSnackbar';
import useStudyInfo from '@core/hooks/useStudyInfo';
import { ReportDocument } from 'app/features/reports/add/components/Form/documentTypes';
import useUploadReport from './useUploadReport';

const useViewerSideReportFunctionality = ({
  studyInstanceUid,
  viewerRef,
  reportRef,
}: {
  studyInstanceUid: string;
  viewerRef: RefObject<HTMLDivElement>;
  reportRef: RefObject<HTMLDivElement>;
}) => {
  const [showSideReport, setShowSideReport] = useState(false);
  const { organizationSettings } = useOrganizationSettings();
  const { studyInfo } = useStudyInfo({ studyInstanceUidOrHash: studyInstanceUid });

  const showSnackbar = useSnackbar();

  const { uploadReport, isUploading } = useUploadReport(studyInstanceUid);

  const handleOpenSideReport = () => {
    const event = new CustomEvent('evodicom.toggleSidePanel');
    document.dispatchEvent(event);
    const tooltipOpen = document.querySelector('.tooltip-toolbar-overlay') as HTMLDivElement;
    if (tooltipOpen) {
      tooltipOpen.style.display = 'none';
    }
    setShowSideReport(true);
  };

  const handleCancelSideReport = () => {
    const event = new CustomEvent('evodicom.toggleSidePanel');
    document.dispatchEvent(event);
    if (viewerRef.current) {
      viewerRef.current.style.width = '100vw';
    }
    setShowSideReport(false);
  };

  const handleUploadReport = async (report: ReportDocument) => {
    if (!studyInstanceUid) return;
    try {
      const response = await uploadReport(report);

      if (response.success) {
        window?.location?.reload?.();
        showSnackbar({
          type: 'success',
          title: 'Reporte creado',
          message: 'El reporte se ha creado.',
        });
        return;
      }

      showSnackbar({
        type: 'error',
        title: 'Ocurrió un error',
        message: 'No ha sido posible crear el reporte.',
      });
    } catch (errorr: any) {
      console.log('Error', errorr);
    }
  };

  useEffect(() => {
    const BORDER_SIZE = 4;
    const viewer = viewerRef.current;
    const report = reportRef.current;
    let mPos: any;

    function resize(e: any) {
      if (viewer) {
        const dx = e.x - mPos;
        mPos = e.x;

        viewer.style.width = parseInt(getComputedStyle(viewer, '').width) + dx + 'px';

        if (report) {
          report.style.width = `calc(100vw - ${viewer.style.width})`;
          report.style.minWidth = `calc(100vw - ${viewer.style.width})`;
        }
      }
    }

    function onMouseDownHandler(e: any) {
      if (viewer) {
        const rect = viewer.getBoundingClientRect();
        if (e.clientX > rect.right - BORDER_SIZE) {
          mPos = e.x;
          document.addEventListener('mousemove', resize, false);
        }
      }
    }

    function onMouseUpHandler() {
      document.removeEventListener('mousemove', resize, false);
    }

    if (showSideReport) {
      viewer?.addEventListener('mousedown', onMouseDownHandler, false);
      document.addEventListener('mouseup', onMouseUpHandler, false);
    }

    return () => {
      if (viewer) {
        document.removeEventListener('mouseup', onMouseUpHandler);
        document.removeEventListener('mousemove', resize);
        viewer.removeEventListener('mousedown', onMouseDownHandler);
      }
    };
  }, [showSideReport]);

  const isDoubleReadingActive: boolean = useMemo(() => {
    if (typeof organizationSettings.StudyDoubleReading === 'string') {
      try {
        const setting = JSON.parse(organizationSettings.StudyDoubleReading);
        if (typeof setting.active !== 'boolean' || !Array.isArray(setting.modalities)) {
          console.log('Incorrectly formatted StudyDoubleReading setting');
          return false;
        }

        if (!setting.active) return false;

        if (!Array.isArray(setting.modalities) || !Array.isArray(studyInfo?.modalities)) {
          return false;
        }

        if (setting.modalities.length === 0 || !studyInfo || studyInfo.modalities.length === 0) {
          return false;
        }

        const hasMatch = studyInfo.modalities.some((studyModality) =>
          setting.modalities.some(
            (settingModality: string) =>
              studyModality?.trim().toLowerCase() === settingModality?.trim().toLowerCase(),
          ),
        );

        return hasMatch;
      } catch (err) {
        console.log('Error parsing StudyDoubleReading setting', err);
        return false;
      }
    }
    return false;
  }, [organizationSettings.StudyDoubleReading, studyInfo]);

  return {
    showSideReport,
    handleUploadReport,
    handleCancelSideReport,
    setShowSideReport,
    handleOpenSideReport,
    isUploading,
    requireDoubleReading: isDoubleReadingActive,
  };
};

export default useViewerSideReportFunctionality;
